import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { Lock, ArrowRight, CheckCircle } from "@phosphor-icons/react";
import Logo from "@/components/Logo";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ResetPassword() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const nav = useNavigate();

    useEffect(() => {
        document.title = "Surveys2CAD | Reset password";
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (password.length < 8) {
            toast.error("Password must be at least 8 characters.");
            return;
        }
        if (password !== confirm) {
            toast.error("Passwords don't match.");
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API}/api/auth/reset-password`, {
                token,
                new_password: password,
            });
            setDone(true);
            toast.success("Password updated. Redirecting to sign in…");
            setTimeout(() => nav("/admin/login", { replace: true }), 1800);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Reset failed.");
        } finally {
            setLoading(false);
        }
    };

    if (!token) {
        return (
            <div
                className="min-h-[100dvh] flex items-center justify-center bg-[#030712] p-4 sm:p-6 grid-bg"
                style={{
                    paddingTop: "max(1rem, env(safe-area-inset-top))",
                    paddingBottom: "max(1rem, env(safe-area-inset-bottom))",
                }}
            >
                <div className="tech-card p-6 sm:p-8 w-full max-w-md text-center" data-testid="reset-missing-token">
                    <Logo size="lg" />
                    <h1 className="font-heading text-2xl font-bold text-slate-50 mt-6">Reset link invalid</h1>
                    <p className="text-slate-400 text-sm mt-3">No token in URL. Use the link from your reset email, or request a new one.</p>
                    <Link to="/admin/login" className="btn-primary justify-center mt-6 inline-flex">
                        Back to sign in <ArrowRight size={14} weight="bold" />
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div
            className="min-h-[100dvh] flex items-center justify-center bg-[#030712] p-4 sm:p-6 grid-bg"
            style={{
                paddingTop: "max(1rem, env(safe-area-inset-top))",
                paddingBottom: "max(1rem, env(safe-area-inset-bottom))",
            }}
        >
            <div className="tech-card p-6 sm:p-8 w-full max-w-md" data-testid="reset-password-card">
                <Logo size="lg" />
                <div className="overline mt-6 mb-1 !text-orange-400">Admin recovery</div>
                <h1 className="font-heading text-3xl font-bold text-slate-50 tracking-tight">Set a new password</h1>
                <p className="text-slate-400 text-sm mt-2">One-time link · expires after use</p>

                {done ? (
                    <div className="mt-8 flex items-center gap-2 text-emerald-400 font-semibold" data-testid="reset-success">
                        <CheckCircle size={20} weight="fill" /> Password updated. Redirecting…
                    </div>
                ) : (
                    <form onSubmit={submit} className="mt-8 space-y-4">
                        <div>
                            <label className="overline mb-2 block">New password</label>
                            <div className="relative">
                                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                                <input
                                    type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)}
                                    className="w-full pl-10 bg-slate-900/70 border border-slate-700 rounded-lg px-3 py-2.5 text-slate-100 focus:outline-none focus:border-cyan-500"
                                    data-testid="reset-new-password"
                                    placeholder="At least 8 characters"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="overline mb-2 block">Confirm password</label>
                            <div className="relative">
                                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                                <input
                                    type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)}
                                    className="w-full pl-10 bg-slate-900/70 border border-slate-700 rounded-lg px-3 py-2.5 text-slate-100 focus:outline-none focus:border-cyan-500"
                                    data-testid="reset-confirm-password"
                                />
                            </div>
                        </div>
                        <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="reset-submit">
                            {loading ? "Updating…" : "Update password"} <ArrowRight size={14} weight="bold" />
                        </button>
                    </form>
                )}

                <p className="text-xs text-slate-500 mt-6 text-center font-mono-alt">
                    Protected area · HTTPS · HttpOnly cookies
                </p>
            </div>
        </div>
    );
}
